// src/app/components/admin/prospect-reads-setting.tsx
//
// Platform section of Global Settings: whether this deployment does PROSPECT
// READS.
//
// A prospect read is a standard production read with reps set to 3 instead of
// 10 — same questions, same models, same scorers, every module populated, real
// data kept in the brand's history permanently. It is how a brand gets read
// before they sign. It is never the baseline: when the prospect signs, their
// first 10-rep run becomes the baseline and the prospect read stays in history.
//
// Turning this on also puts demo and prospect brands in the brand list for
// EVERY login, which is what makes a new prospect brand show up for the whole
// team without anyone assigning it per-user. Real clients are untouched by
// that — they stay assignment-only.
//
// The value lives in app_config on the backend and is read per request, so a
// change takes effect immediately with no deploy and no restart. Sandbox and
// production are separate databases, so each holds its own answer.

import { useEffect, useState } from "react";
import { Check, Loader2, Users, XCircle, Info } from "lucide-react";

import {
  fetchProspectReads,
  updateProspectReads,
  type ProspectReadsState,
} from "@/app/services/settings/prospect-reads-api";

interface Choice {
  enabled: boolean;
  label: string;
  desc: string;
  icon: typeof Users;
}

const CHOICES: Choice[] = [
  {
    enabled: false,
    label: "Off — paying clients only",
    desc:
      "No brand can be read as a prospect, and the brand list stays strictly per-user. This is the normal posture for production.",
    icon: XCircle,
  },
  {
    enabled: true,
    label: "On — prospect reads allowed",
    desc:
      "A brand can be marked a prospect and read at 3 reps. Demo and prospect brands then appear for every login, beside each user's own assigned brands.",
    icon: Users,
  },
];

export function ProspectReadsSetting() {
  const [state, setState] = useState<ProspectReadsState | null>(null);
  const [saving, setSaving] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    let alive = true;
    fetchProspectReads()
      .then((next) => alive && setState(next))
      .catch((e) => alive && setErr(String(e?.message || e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  async function choose(enabled: boolean) {
    if (enabled === state?.enabled || saving !== null) return;
    setSaving(enabled);
    setErr("");
    try {
      const stored = await updateProspectReads(enabled);
      setState(stored);
      setSavedAt(Date.now());
    } catch (e: any) {
      setErr(String(e?.message || e));
    } finally {
      setSaving(null);
    }
  }

  const busy = loading || saving !== null;

  return (
    <div className="py-6">
      <div className="max-w-2xl">
        <h2 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)" }}>
          Prospect reads
        </h2>
        <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginTop: 4 }}>
          A prospect read is a full measurement at 3 repeats instead of 10 — the same questions,
          models and scorers, every module populated, kept in the brand's history permanently. It is
          never the baseline: when a prospect signs, their first 10-repeat run becomes the baseline
          and the prospect read stays in history. Takes effect immediately.
        </p>

        {err && (
          <div
            className="mt-4 rounded-lg px-3 py-2"
            style={{ fontSize: "var(--text-sm)", color: "var(--destructive)", backgroundColor: "var(--destructive-10, rgba(220,38,38,0.08))" }}
          >
            {err}
          </div>
        )}

        <div className="mt-5 flex flex-col gap-3">
          {CHOICES.map((choice) => {
            const Icon = choice.icon;
            const isCurrent = state?.enabled === choice.enabled;
            const isSaving = saving === choice.enabled;
            return (
              <button
                key={String(choice.enabled)}
                type="button"
                disabled={busy}
                onClick={() => choose(choice.enabled)}
                className="flex items-start gap-3 rounded-xl border p-4 text-left transition-all"
                style={{
                  cursor: busy ? "default" : "pointer",
                  borderColor: isCurrent ? "var(--alpha-teal)" : "var(--border)",
                  backgroundColor: isCurrent ? "var(--alpha-teal-10)" : "var(--card, transparent)",
                  boxShadow: isCurrent ? "0 0 0 1px var(--alpha-teal)" : "none",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                <span
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
                  style={{ backgroundColor: isCurrent ? "var(--alpha-teal)" : "var(--muted, #f1f5f9)", color: isCurrent ? "#fff" : "var(--muted-foreground)" }}
                >
                  <Icon className="h-4.5 w-4.5" />
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)" }}>
                      {choice.label}
                    </span>
                    {isCurrent && !isSaving && (
                      <span className="inline-flex items-center gap-1" style={{ fontSize: "var(--text-xs)", color: "var(--alpha-teal)", fontWeight: "var(--font-weight-medium)" }}>
                        <Check className="h-3.5 w-3.5" /> Active
                      </span>
                    )}
                    {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: "var(--alpha-teal)" }} />}
                  </span>
                  <span className="mt-0.5 block" style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
                    {choice.desc}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {/* Where the current value came from. "On because this is a sandbox"
            and "on because somebody turned it on" are different facts, and an
            admin deciding whether to touch this needs to know which one they
            are looking at. */}
        {state && !loading && (
          <div
            className="mt-4 flex items-start gap-2 rounded-lg px-3 py-2"
            style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", backgroundColor: "var(--muted, #f1f5f9)" }}
          >
            <Info className="h-4 w-4 flex-shrink-0" style={{ marginTop: 2, color: "var(--muted-foreground)" }} />
            <span>
              {state.source === "environment_default" ? (
                <>
                  No one has set this. It is {state.enabled ? "on" : "off"} because this deployment
                  runs as <strong>{state.environment}</strong>
                  {state.enabled
                    ? " — sandboxes get prospect reads by default."
                    : " — production does not get prospect reads unless you turn them on here."}
                </>
              ) : (
                <>
                  Set by an admin. This deployment runs as <strong>{state.environment}</strong>,
                  where the default would be {state.environment_default ? "on" : "off"}.
                </>
              )}
            </span>
          </div>
        )}

        {savedAt > 0 && saving === null && !err && (
          <p className="mt-3" style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
            Saved. Prospect reads are now {state?.enabled ? "allowed" : "off"}.
          </p>
        )}
      </div>
    </div>
  );
}

export default ProspectReadsSetting;
